// ✅ SupervisorPaymentListing.jsx (REDESIGNED — EDIT/SELECT COLUMNS + RESULTS SUMMARY)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaTrash,
} from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

const SupervisorPaymentListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(true);

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [branches, setBranches] = useState([]);
  const [nameDDL, setNameDDL] = useState([]);
  const [codeDDL, setCodeDDL] = useState([]);
  const [entryDDL, setEntryDDL] = useState([]);

  const defaultSearchData = {
    fromDate: "",
    toDate: "",
    supervisorid: "",
    branchid: branchId || "",
    entryno: "",
  };

  const [searchData, setSearchData] = useState(defaultSearchData);

  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ selected payment ids
  const [selectedIds, setSelectedIds] = useState([]);

  const allVisibleIds = useMemo(
    () => (paymentData || []).map((x) => x.paymentId).filter(Boolean),
    [paymentData]
  );

  const isAllSelected = useMemo(() => {
    if (allVisibleIds.length === 0) return false;
    return allVisibleIds.every((id) => selectedIds.includes(id));
  }, [allVisibleIds, selectedIds]);

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // ---------------------------
  // API Calls
  // ---------------------------
  const loadBranches = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/branches`, {
        headers: commonHeaders,
      });
      setBranches(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load branches");
      setBranches([]);
    }
  };

  const loadNameDDL = async (bid) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/nameDDL`, {
        params: bid ? { branchid: bid } : {},
        headers: commonHeaders,
      });
      setNameDDL(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load supervisor names");
      setNameDDL([]);
    }
  };

  const loadCodeDDL = async (bid) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/codeDDL`, {
        params: bid ? { branchid: bid } : {},
        headers: commonHeaders,
      });
      setCodeDDL(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load supervisor codes");
      setCodeDDL([]);
    }
  };

  const loadEntryDDL = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/entryNoDDL`, {
        headers: commonHeaders,
      });
      setEntryDDL(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load entry numbers");
      setEntryDDL([]);
    }
  };

  const loadList = async () => {
    try {
      setLoading(true);

      if (!searchData.branchid) {
        toast.warn("Please select Branch");
        setPaymentData([]);
        return;
      }

      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/list`, {
        params: {
          branchid: searchData.branchid,
          supervisorid: searchData.supervisorid || "",
          fromDate: searchData.fromDate || "",
          toDate: searchData.toDate || "",
          entryno: searchData.entryno || "",
        },
        headers: commonHeaders,
      });

      if (res.data?.success === false) {
        toast.error(res.data?.message || "Error loading data");
        setPaymentData([]);
      } else {
        setPaymentData(Array.isArray(res.data?.data) ? res.data.data : []);
      }

      // ✅ reset selection on new search
      setSelectedIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
      setPaymentData([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Clear filters (branch stays as-is since it drives dependent dropdowns) and reload
  const handleClearFilters = () => {
    setSearchData((prev) => ({
      ...defaultSearchData,
      branchid: prev.branchid,
    }));
    setTimeout(() => loadList(), 0);
  };

  // ---------------------------
  // Selection Handlers
  // ---------------------------
  const toggleRow = (paymentId) => {
    setSelectedIds((prev) => {
      if (prev.includes(paymentId)) return prev.filter((x) => x !== paymentId);
      return [...prev, paymentId];
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (isAllSelected) return [];
      return allVisibleIds;
    });
  };

  // ---------------------------
  // Delete Selected (bulk, via Remove button)
  // ---------------------------
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one row to delete.");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      await Promise.all(
        selectedIds.map((paymentid) =>
          axios.delete(`${BASE_URL}/api/supervisorPayment/delete`, {
            params: { paymentid },
            headers: commonHeaders,
          })
        )
      );

      toast.success("Deleted successfully");
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Update — same navigation, now triggered per-row via Edit icon
  // ---------------------------
  const handleUpdateRow = (paymentId) => {
    if (!paymentId) {
      toast.warn("Invalid row");
      return;
    }

    navigate("/PaymentVouchers/SupervisorPaymentEntry", {
      state: { paymentid: paymentId, editflag: true },
    });
  };

  // ---------------------------
  // Effects
  // ---------------------------
  useEffect(() => {
    loadBranches();
    loadEntryDDL();
    loadNameDDL(branchId || "");
    loadCodeDDL(branchId || "");

    if (branchId) {
      loadList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchData.branchid) {
      loadNameDDL(searchData.branchid);
      loadCodeDDL(searchData.branchid);
      setSearchData((prev) => ({ ...prev, supervisorid: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData.branchid]);

  const handleAddNew = () => navigate("/PaymentVouchers/SupervisorPaymentEntry");
  const handleSearch = () => loadList();

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SUPERVISOR PAYMENT LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Supervisor Payment Listing
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch /> {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={searchData.fromDate}
                    onChange={(e) => handleSearchChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={searchData.toDate}
                    onChange={(e) => handleSearchChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Branch Name
                  </label>
                  <select
                    value={searchData.branchid}
                    disabled={branch !== "HO"}
                    onChange={(e) => handleSearchChange("branchid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_branchid} value={b.fld_branchid}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supervisor Name
                  </label>
                  <select
                    value={searchData.supervisorid}
                    onChange={(e) => handleSearchChange("supervisorid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {nameDDL.map((x) => (
                      <option key={x.fld_supervisorid} value={x.fld_supervisorid}>
                        {x.fld_supervisorname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supervisor Code
                  </label>
                  <select
                    value={searchData.supervisorid}
                    onChange={(e) => handleSearchChange("supervisorid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {codeDDL.map((x) => (
                      <option key={x.fld_supervisorid} value={x.fld_supervisorid}>
                        {x.fld_supervisorcode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Entry Number
                  </label>
                  <select
                    value={searchData.entryno}
                    onChange={(e) => handleSearchChange("entryno", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {entryDDL.map((x) => (
                      <option key={x.fld_paymentid} value={x.fld_entryno}>
                        {x.fld_entryno}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl.No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Entry Number</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Supervisor Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Branch Name</th>

                  {/* EDIT - SECOND LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>

                  {/* SELECT ALL - LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={7} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : paymentData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  paymentData.map((row, idx) => {
                    const checked = selectedIds.includes(row.paymentId);

                    return (
                      <tr
                        key={row.paymentId}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row.slNo}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row.date}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {row.entryNumber}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {row.supervisorName}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row.branchName}
                        </td>

                        {/* EDIT */}
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleUpdateRow(row.paymentId)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        {/* SELECT */}
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(row.paymentId)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* ==================== REMOVE BUTTON ==================== */}
            <div className="p-4">
              <button
                onClick={handleDeleteSelected}
                disabled={loading || selectedIds.length === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                  loading || selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                } bg-red-600 hover:bg-red-700 text-white`}
              >
               🗑
                Remove
              </button>
            </div>
          </div>

          {/* ==================== TOAST ==================== */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default SupervisorPaymentListing;